<!--
  TARIFFE — sul motore a fasce.

  Legge e scrive `tariff_band`:
    - tariffa Simba (`is_partner_share = false`): quanto Simba fattura al partner;
    - tariffa del partner (`is_partner_share = true`): quanto paga il suo cliente.

  Chi vede cosa:
    - amministratore del partner: entrambe, e imposta la sua (mai sotto Simba);
    - dipendente e venditore: solo la tariffa del partner, in sola lettura.
  Lo decide il backend (`GET /tariff-band/partner-tariffs`): a quei due ruoli
  le tariffe Simba non arrivano proprio, la pagina non deve nasconderle.

  Le fasce non si modificano: il backend chiude quella in vigore e ne crea una
  nuova valida da oggi. La struttura (km, operatori, modo di calcolo) è quella
  della fascia Simba: il partner sceglie solo gli importi.

  Gli spostamenti interni possono avere DUE listini: quello normale e quello dei
  veicoli non immatricolati, che si spostano con la targa prova. Stanno sugli
  stessi km e operatori, quindi fanno card separate (`immatricolato = false`).
-->
<template>
  <div class="tp">
    <header class="tp__head">
      <div>
        <h1 class="tp__title">
          Tariffe
        </h1>
        <p
          v-if="modifica"
          class="tp__sub"
        >
          Per ogni fascia di distanza trovi quanto ti fattura Simba e quanto fai
          pagare al tuo cliente. La tua tariffa non può essere inferiore a quella
          Simba e vale per le corse da oggi in poi.
        </p>
        <p
          v-else
          class="tp__sub"
        >
          Quanto paga il cliente per ogni servizio, secondo la distanza. Le
          tariffe le imposta l'amministratore.
        </p>
      </div>
    </header>

    <div
      v-if="stato === 'carico'"
      class="tp__stato"
    >
      Carico le tariffe…
    </div>

    <div
      v-else-if="stato === 'errore'"
      class="tp__stato tp__stato--ko"
      role="alert"
    >
      Non sono riuscito a caricare le tariffe.
      <el-button
        size="small"
        @click="carica"
      >
        Riprova
      </el-button>
    </div>

    <div
      v-else-if="!gruppi.length"
      class="tp__stato"
    >
      Sul tuo contratto non risulta nessuna tariffa dei servizi. Parlane con Simba.
    </div>

    <template v-else>
      <section
        v-for="g in gruppi"
        :key="g.chiave"
        class="tp__card"
      >
        <header class="tp__cardhead">
          <h2 class="tp__cardtitle">
            {{ g.titolo }}
          </h2>
          <p
            v-if="g.nota"
            class="tp__cardnote"
          >
            {{ g.nota }}
          </p>
        </header>

        <div class="tp__rows">
          <div
            class="tp__row tp__row--head"
            :class="{ 'tp__row--lettura': !modifica }"
          >
            <span class="tp__col">Fascia</span>
            <span
              v-if="modifica"
              class="tp__col"
            >Tariffa Simba</span>
            <span class="tp__col">{{ modifica ? 'La tua tariffa' : 'Tariffa' }}</span>
          </div>

          <div
            v-for="r in g.righe"
            :key="r.chiave"
            class="tp__row"
            :class="{ 'tp__row--lettura': !modifica }"
          >
            <span class="tp__col tp__col--fascia">{{ r.fascia }}</span>

            <span
              v-if="modifica"
              class="tp__col tp__col--simba"
            >
              {{ r.simba ? prezzo(r.simba, r.min_km) : '—' }}
            </span>

            <div class="tp__col">
              <template v-if="modifica && r.simba_band_id">
                <div class="tp__inputs">
                  <el-input
                    v-model="bozza[r.chiave].price"
                    type="number"
                    step="0.01"
                    min="0"
                    size="small"
                    :aria-label="`${g.titolo}, ${r.fascia}: la tua tariffa`"
                  >
                    <template #prepend>
                      €
                    </template>
                    <template #append>
                      {{ r.simba.pricing_mode === 'per_km' ? '/km' : 'a corsa' }}
                    </template>
                  </el-input>
                  <el-input
                    v-if="conEccedenza(r.simba)"
                    v-model="bozza[r.chiave].excess"
                    type="number"
                    step="0.01"
                    min="0"
                    size="small"
                    :aria-label="`${g.titolo}, ${r.fascia}: euro al km oltre la fascia`"
                  >
                    <template #prepend>
                      + €
                    </template>
                    <template #append>
                      /km
                    </template>
                  </el-input>
                </div>
                <div
                  v-if="errori[r.chiave]"
                  class="tp__err"
                >
                  {{ errori[r.chiave] }}
                </div>
                <div
                  v-else-if="r.partner_scope === 'global'"
                  class="tp__hint"
                >
                  Tariffa generale Simba: se la salvi diventa tua.
                </div>
                <div
                  v-else-if="!r.partner"
                  class="tp__hint"
                >
                  Non impostata.
                </div>
              </template>

              <span
                v-else
                class="tp__prezzo"
                :class="{ 'is-vuota': !r.partner }"
              >
                {{ r.partner ? prezzo(r.partner, r.min_km) : 'Non impostata' }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div
        v-if="modifica"
        class="tp__azioni"
      >
        <span class="tp__hint">{{ riepilogoModifiche }}</span>
        <el-button
          type="primary"
          :loading="salvataggio"
          :disabled="!modificate.length"
          @click="salva"
        >
          Salva tariffe
        </el-button>
      </div>
    </template>

    <!-- Il valet ha un listino suo, a fasce km, con il suo salvataggio. -->
    <ValetTariffeSection
      v-if="store.bookingType && stato !== 'carico'"
      :modifica="modifica"
    />
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import partnerAPI from '../services/partnerAPI.js';
import { isLoggedStore } from '../store/logged';
import { isPartnerAdmin } from '../services/session';
import ValetTariffeSection from '../Components/ValetTariffeSection.vue';

// Le corse con un veicolo non immatricolato (si sposta con la targa prova)
// hanno un listino loro: stessa struttura, importi diversi.
const ETICHETTA_PROVA = ' · targa prova';
const NOTA_PROVA = 'Vale per le corse con un veicolo non immatricolato, che si sposta con la targa prova.';

const SERVIZI = {
  pickup: { titolo: 'Pickup & Delivery' },
  pickup_loaner: { titolo: 'Pickup & Delivery con auto di cortesia' },
  // Un partner notturno ha questi due al posto del pickup: il listino serale
  // lo sceglie il server dall'ora della corsa (22–06).
  restaurant: { titolo: 'Notturno', nota: 'Accompagniamo il cliente la sera e lo riportiamo a casa.' },
  restaurant_night: { titolo: 'Notturno · fascia serale', nota: 'Corse che partono fra le 22 e le 6.' },
  escort: { titolo: 'Transfer', nota: "Accompagniamo il cliente da un punto a un altro con l'auto Simba." },
  escort_loaner: { titolo: 'Transfer con auto del partner' },
  journey: { titolo: 'Spostamenti interni' },
};

/** Un valore c'e' davvero? `0` e' un valore, `null` e `''` no. */
const valorizzato = (v) => v !== null && v !== undefined && String(v).trim() !== '';
const km = (v) => Number(v).toLocaleString('it-IT');
const euro = (v) =>
  `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// «prezzo + €/km»: la fascia aperta con la parte chilometrica.
const conEccedenza = (p) => p && ['flat_plus_km_excess', 'flat_plus_km_total'].includes(p.pricing_mode);
// L'importo principale della fascia: €/km se è al km, altrimenti il prezzo della corsa.
const importoPrincipale = (p) => (p.pricing_mode === 'per_km' ? p.price_per_km : p.flat_price ?? 0);

function prezzo(p, minKm) {
  switch (p.pricing_mode) {
    case 'per_km':
      return `${euro(p.price_per_km)}/km`;
    case 'flat':
      return `${euro(p.flat_price ?? 0)} a corsa`;
    case 'flat_plus_km_excess':
      return `${euro(p.flat_price ?? 0)} + ${euro(p.price_per_km)}/km oltre ${km(minKm)} km`;
    case 'flat_plus_km_total':
      return `${euro(p.flat_price ?? 0)} + ${euro(p.price_per_km)}/km`;
    default:
      return '—';
  }
}

function fascia(r) {
  if (r.max_km === null || r.max_km === undefined) return `oltre ${km(r.min_km)} km`;
  if (!Number(r.min_km)) return `fino a ${km(r.max_km)} km`;
  return `da ${km(r.min_km)} a ${km(r.max_km)} km`;
}

export default {
  name: 'TariffePage',
  components: { ValetTariffeSection },

  setup() {
    const store = isLoggedStore();
    const stato = ref('carico'); // carico | ok | errore
    const dati = ref(null);
    const bozza = reactive({});
    const base = reactive({});
    const errori = reactive({});
    const salvataggio = ref(false);

    // Chi modifica lo dice il backend; finché non ha risposto vale il ruolo.
    const modifica = computed(() => dati.value?.can_edit ?? isPartnerAdmin(store.role));

    const gruppi = computed(() =>
      (dati.value?.groups || []).map((g) => {
        const servizio = SERVIZI[g.service] || { titolo: g.service };
        // Sugli spostamenti 2 operatori = singolo, 1 = a catena.
        const operatori = g.operators ? ` · ${g.operators === 1 ? 'a catena' : 'singolo'}` : '';
        // La variante fa parte della chiave: il listino della targa prova ha
        // gli stessi km e operatori di quello normale, e senza finirebbero
        // nella stessa card (e con la stessa chiave di riga nella bozza).
        const prova = g.immatricolato === false;
        const vari = prova ? 'prova' : 'std';
        return {
          chiave: `${g.service}|${vari}|${g.operators ?? 'any'}`,
          titolo: `${servizio.titolo}${prova ? ETICHETTA_PROVA : ''}${operatori}`,
          nota: prova ? NOTA_PROVA : servizio.nota,
          prova,
          righe: g.rows.map((r) => ({
            ...r,
            chiave: `${g.service}|${vari}|${g.operators ?? 'any'}|${r.min_km}|${r.max_km ?? 'inf'}`,
            fascia: fascia(r),
          })),
        };
      }),
    );
    const modificabili = computed(() => gruppi.value.flatMap((g) => g.righe).filter((r) => r.simba_band_id));

    // La bozza parte dalla tariffa del partner, se ha lo stesso modo di calcolo
    // della fascia Simba (altrimenti i due numeri non sarebbero confrontabili).
    const riempiBozza = () => {
      [bozza, base, errori].forEach((o) => Object.keys(o).forEach((k) => delete o[k]));
      modificabili.value.forEach((r) => {
        const stessoModo = r.partner && r.partner.pricing_mode === r.simba.pricing_mode;
        const valori = {
          price: stessoModo ? String(importoPrincipale(r.partner)) : '',
          excess: stessoModo && conEccedenza(r.simba) ? String(r.partner.price_per_km) : '',
        };
        bozza[r.chiave] = { ...valori };
        base[r.chiave] = { ...valori };
      });
    };

    const applica = (risposta) => {
      dati.value = risposta;
      riempiBozza();
    };

    const carica = async () => {
      stato.value = 'carico';
      try {
        const res = await partnerAPI.getPartnerTariffs();
        applica(res.data);
        stato.value = 'ok';
      } catch (err) {
        console.error('Tariffe non caricate:', err?.response?.status);
        stato.value = 'errore';
      }
    };
    onMounted(carica);

    const diverso = (a, b) => {
      if (!valorizzato(a) || !valorizzato(b)) return valorizzato(a) !== valorizzato(b);
      return Number(a) !== Number(b);
    };
    const modificate = computed(() =>
      modificabili.value.filter((r) => {
        const b = bozza[r.chiave];
        const o = base[r.chiave];
        return b && o && (diverso(b.price, o.price) || diverso(b.excess, o.excess));
      }),
    );
    const riepilogoModifiche = computed(() => {
      const n = modificate.value.length;
      if (!n) return 'Nessuna modifica da salvare';
      return n === 1 ? '1 tariffa da salvare' : `${n} tariffe da salvare`;
    });

    // Il controllo lo rifà il server; qui serve a dirlo accanto al campo.
    const valida = () => {
      Object.keys(errori).forEach((k) => delete errori[k]);
      modificate.value.forEach((r) => {
        const b = bozza[r.chiave];
        const simba = Number(importoPrincipale(r.simba));
        if (!valorizzato(b.price)) {
          errori[r.chiave] = 'Inserisci un importo: una tariffa si può cambiare, non togliere.';
        } else if (Number.isNaN(Number(b.price)) || Number(b.price) < 0) {
          errori[r.chiave] = 'Inserisci un importo non negativo.';
        } else if (Number(b.price) < simba) {
          errori[r.chiave] = `Non può essere inferiore alla tariffa Simba (${prezzo(r.simba, r.min_km)}).`;
        } else if (conEccedenza(r.simba) && valorizzato(b.excess)) {
          if (Number.isNaN(Number(b.excess)) || Number(b.excess) < Number(r.simba.price_per_km)) {
            errori[r.chiave] = `Il costo al km non può essere inferiore a quello Simba (${euro(r.simba.price_per_km)}/km).`;
          }
        }
      });
      return !Object.keys(errori).length;
    };

    const salva = async () => {
      if (!valida()) {
        ElMessage({ type: 'error', message: 'Controlla le tariffe segnalate.' });
        return;
      }
      salvataggio.value = true;
      try {
        const prices = modificate.value.map((r) => {
          const b = bozza[r.chiave];
          const voce = { simba_band_id: r.simba_band_id, price: Number(b.price) };
          if (conEccedenza(r.simba) && valorizzato(b.excess)) voce.excess_per_km = Number(b.excess);
          return voce;
        });
        const res = await partnerAPI.setPartnerTariffs(prices);
        applica(res.data);
        ElMessage({ type: 'success', message: 'Tariffe salvate: valgono per le corse da oggi.' });
      } catch (err) {
        ElMessage({
          type: 'error',
          showClose: true,
          message: err?.response?.data?.message || 'Tariffe non salvate. Riprova.',
        });
      } finally {
        salvataggio.value = false;
      }
    };

    return {
      store, stato, modifica, gruppi, bozza, errori, salvataggio, modificate, riepilogoModifiche,
      carica, salva, prezzo, conEccedenza,
    };
  },
};
</script>

<style scoped>
.tp { padding: 4px 8px 48px; text-align: left; display: flex; flex-direction: column; gap: 16px; }

.tp__head { display: flex; align-items: flex-start; gap: 14px; }
.tp__title { margin: 0; font-size: 23px; font-weight: 700; letter-spacing: -0.015em; color: var(--color-text-primary); }
.tp__sub { margin: 4px 0 0; font-size: 13px; line-height: 1.5; color: var(--color-text-secondary); max-width: 62ch; }

.tp__stato { display: flex; align-items: center; gap: 12px; font-size: 13px; color: var(--color-text-secondary); background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 18px; }
.tp__stato--ko { color: var(--state-danger); border-color: var(--state-danger-line); background: var(--state-danger-bg); font-weight: 600; }

.tp__card { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; box-shadow: var(--el-box-shadow-lighter); overflow: hidden; }
.tp__cardhead { padding: 13px 18px; border-bottom: 1px solid var(--color-border); }
.tp__cardtitle { margin: 0; font-size: 14.5px; font-weight: 600; }
.tp__cardnote { margin: 3px 0 0; font-size: 12.5px; color: var(--color-text-secondary); }

.tp__rows { padding: 6px 18px 14px; }
.tp__row { display: grid; grid-template-columns: 1fr 1.2fr 1.6fr; gap: 18px; align-items: center; padding: 7px 0; }
.tp__row--lettura { grid-template-columns: 1fr 2fr; }
.tp__row--head { padding: 10px 0 6px; border-bottom: 1px solid var(--color-border); }
.tp__row--head .tp__col { font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary); }
.tp__col { min-width: 0; }
.tp__col--fascia { font-size: 13.5px; font-weight: 600; }
/* Cifre in colonna: `tabular-nums` le incolonna anche quando le larghezze dei
   glifi differiscono, cosi' i decimali si leggono uno sotto l'altro. */
.tp__col--simba { font-size: 13.5px; font-variant-numeric: tabular-nums; color: var(--color-text-secondary); }
.tp__prezzo { font-size: 14px; font-weight: 600; font-variant-numeric: tabular-nums; }
.tp__prezzo.is-vuota { font-weight: 400; color: var(--color-text-muted); }

.tp__inputs { display: flex; flex-wrap: wrap; gap: 8px; }
.tp__inputs :deep(.el-input) { width: 200px; }
.tp__err { margin-top: 4px; font-size: 11.5px; font-weight: 600; color: var(--state-danger); }
.tp__hint { margin-top: 3px; font-size: 12px; color: var(--color-text-muted); }

.tp__azioni { display: flex; align-items: center; justify-content: flex-end; gap: 12px; flex-wrap: wrap; padding-top: 4px; }
.tp__azioni .tp__hint { margin-top: 0; }

@media (max-width: 760px) {
  .tp__row, .tp__row--lettura { grid-template-columns: 1fr; gap: 4px; }
  .tp__row--head { display: none; }
  .tp__col--simba::before { content: 'Tariffa Simba: '; font-weight: 600; }
}
</style>
